import React from 'react';

import { PromoIcon } from 'apps/promo/components/common/promo-icon/PromoIcon';
import { PromoText } from 'apps/promo/components/common/promo-text/PromoText';
import clsx from 'clsx';

import styles from './PromoDiskButton.module.css';

type PromoDiskButtonType = 'button' | 'pseudo' | 'link';
type PromoDiskButtonVariant = 'primary' | 'secondary';
type PromoDiskButtonSize = 'small' | 'large';

export interface PromoDiskButtonProps {
    type?: PromoDiskButtonType;
    variant?: PromoDiskButtonVariant;
    size?: PromoDiskButtonSize;
    isInverted?: boolean;
    isDisabled?: boolean;
    isSelected?: boolean;
    isNavButton?: boolean;
    isNavButtonInverted?: boolean;
    iconName?: string;
    caption?: string | number;
    href?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement>;
    labelText?: string;
}

export const PromoDiskButton: React.FC<PromoDiskButtonProps> = ({
    type,
    variant = 'secondary',
    size = 'small',
    isInverted,
    isDisabled,
    isSelected,
    isNavButton,
    isNavButtonInverted,
    iconName,
    caption,
    href,
    onClick,
    labelText,
}) => {
    const Tag: React.ElementType = type === 'button' ? 'button' : type === 'pseudo' ? 'span' : 'a';

    const sizeClass = size === 'large' ? styles.sizeLarge : styles.sizeSmall;

    const className = clsx(styles.commonButton, sizeClass, {
        [styles.disabled]: isDisabled,
        [styles.selected]: isSelected,
        [styles.primary]: variant === 'primary',
        [styles.inverted]: isInverted,
        [styles.navButton]: isNavButton,
        [styles.navButtonInverted]: isNavButtonInverted,
        [styles.hoverable]: !isDisabled,
    });

    return (
        <Tag aria-label={labelText} href={href} onClick={onClick} type={type === 'button' ? 'button' : undefined} className={className}>
            {iconName && (
                <span
                    className={clsx(styles.iconContainer, {
                        [styles.iconSmall]: size === 'small',
                        [styles.iconLarge]: size === 'large',
                    })}
                >
                    <PromoIcon name={iconName} />
                </span>
            )}

            {caption && (
                <PromoText tag="span" variant="subheader" color="primary">
                    {caption}
                </PromoText>
            )}
        </Tag>
    );
};
